import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import jsPDF from 'jspdf';
import JSZip from 'jszip';

/* ------------------------------------------------------------------ */
/*  Pure layout math — no React, no DOM. Kept separate on purpose so   */
/*  the same functions drive both the canvas preview and the PDF.      */
/* ------------------------------------------------------------------ */

const DEFAULT_SETTINGS = {
  pageWidth: 460, // mm
  pageHeight: 305, // mm
  margin: 5, // mm, all sides
  photoWidth: 35, // mm
  photoHeight: 45, // mm
  gap: 1, // mm, between every photo
  // Optional manual cap on photos per box. Leave null/0 to always use the
  // maximum number that physically fits (see calculateMaxFit below).
  photosPerSectionOverride: null,
};

const PREVIEW_SCALE = 2.2; // px per mm on screen

// Split the printable area into 4 equal quadrants (TL, TR, BL, BR).
function getSections(pageWidth, pageHeight, margin) {
  const printableW = pageWidth - margin * 2;
  const printableH = pageHeight - margin * 2;
  const halfW = printableW / 2;
  const halfH = printableH / 2;
  return [
    { name: 'Top Left', x: margin, y: margin, w: halfW, h: halfH },
    { name: 'Top Right', x: margin + halfW, y: margin, w: halfW, h: halfH },
    { name: 'Bottom Left', x: margin, y: margin + halfH, w: halfW, h: halfH },
    { name: 'Bottom Right', x: margin + halfW, y: margin + halfH, w: halfW, h: halfH },
  ];
}

// How many fixed-size photos physically fit in a box, filling it as
// completely as possible (no overflow, no manual count needed).
// e.g. a 225 x 147.5mm box with 35x45mm photos and a 1mm gap fits
// 6 columns x 3 rows = 18 photos.
function calculateMaxFit(sectionW, sectionH, photoW, photoH, gap) {
  const cols = Math.max(1, Math.floor((sectionW + gap) / (photoW + gap)));
  const rows = Math.max(1, Math.floor((sectionH + gap) / (photoH + gap)));
  return { cols, rows, count: cols * rows };
}

// Given a section rectangle and how many fixed-size photos must live
// inside it, find the grid (cols x rows) that uses the most area
// without overflowing, then center the grid — and center any
// partially-filled last row — inside the section.
function calculateSectionLayout(section, count, photoW, photoH, gap) {
  if (count <= 0) return [];

  let best = null;
  for (let cols = 1; cols <= count; cols++) {
    const rows = Math.ceil(count / cols);
    const gridW = cols * photoW + (cols - 1) * gap;
    const gridH = rows * photoH + (rows - 1) * gap;
    if (gridW <= section.w + 1e-6 && gridH <= section.h + 1e-6) {
      const area = gridW * gridH;
      if (!best || area > best.area) best = { cols, rows, gridW, gridH, area };
    }
  }

  // Fallback (only hit if the section is too small for even 1 column):
  // stack vertically anyway rather than silently dropping photos.
  if (!best) {
    best = {
      cols: 1,
      rows: count,
      gridW: photoW,
      gridH: count * photoH + (count - 1) * gap,
    };
  }

  const { cols, rows, gridW, gridH } = best;
  const offsetX = section.x + (section.w - gridW) / 2;
  const offsetY = section.y + (section.h - gridH) / 2;

  const positions = [];
  let idx = 0;
  for (let r = 0; r < rows && idx < count; r++) {
    const remaining = count - idx;
    const colsInRow = Math.min(cols, remaining);
    const rowW = colsInRow * photoW + (colsInRow - 1) * gap;
    const rowOffsetX = offsetX + (gridW - rowW) / 2;
    for (let c = 0; c < colsInRow; c++) {
      positions.push({
        x: rowOffsetX + c * (photoW + gap),
        y: offsetY + r * (photoH + gap),
        w: photoW,
        h: photoH,
      });
      idx++;
    }
  }
  return positions;
}

// Full-page layout: sections + per-section counts + flattened photo
// positions in page (mm) coordinates. Never hardcodes X/Y — everything
// is derived from settings. Every box auto-fills to the maximum number
// of photos that physically fit, unless the user set a lower override.
function calculateFullLayout(settings) {
  const { pageWidth, pageHeight, margin, photoWidth, photoHeight, gap, photosPerSectionOverride } = settings;
  const sections = getSections(pageWidth, pageHeight, margin);
  const maxFit = calculateMaxFit(sections[0].w, sections[0].h, photoWidth, photoHeight, gap);
  const perSection =
    photosPerSectionOverride && photosPerSectionOverride > 0
      ? Math.min(photosPerSectionOverride, maxFit.count)
      : maxFit.count;
  const counts = sections.map(() => perSection);
  let positions = [];
  sections.forEach((sec, i) => {
    positions = positions.concat(calculateSectionLayout(sec, counts[i], photoWidth, photoHeight, gap));
  });
  return { sections, counts, positions, maxFit, perSection };
}

// Split the uploaded images into pages according to the upload mode.
//  - single-repeat: one image repeated into every slot on the sheet.
//  - single-center: one image, centered on the whole page.
//  - bulk / zip with <= 4 images: each image is assigned to its own box
//    and repeated to fill that box completely (box 1 = image 1, box 2 =
//    image 2, ...). Unused boxes are left as placeholders.
//  - bulk / zip with more images than boxes: fills sequentially across
//    the whole sheet, spilling onto extra PDF pages once it runs out
//    of space.
function buildPages(images, layout, uploadMode) {
  if (images.length === 0) return [[]];

  const capacity = layout.positions.length; // total slots on one sheet

  if (uploadMode === 'single-repeat') {
    return [Array(capacity).fill(images[0])];
  }
  if (uploadMode === 'single-center') {
    return [[images[0]]];
  }

  // bulk & zip
  if (images.length <= layout.sections.length) {
    const pageImages = [];
    layout.sections.forEach((sec, i) => {
      const img = images[i] || null;
      for (let j = 0; j < layout.counts[i]; j++) pageImages.push(img);
    });
    return [pageImages];
  }

  const pages = [];
  for (let i = 0; i < images.length; i += capacity) {
    pages.push(images.slice(i, i + capacity));
  }
  return pages;
}

function getImageFormat(dataUrl) {
  if (dataUrl.startsWith('data:image/png')) return 'PNG';
  if (dataUrl.startsWith('data:image/webp')) return 'WEBP';
  return 'JPEG';
}

/* ------------------------------------------------------------------ */
/*  Canvas drawing helpers                                             */
/* ------------------------------------------------------------------ */

function drawScissors(ctx, xMM, yMM, scale, rotation) {
  const x = xMM * scale;
  const y = yMM * scale;
  const s = 4.5;
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(rotation);
  ctx.strokeStyle = '#9aa0a6';
  ctx.fillStyle = '#ffffff';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(0, 0);
  ctx.lineTo(s * 2.2, -s * 1.1);
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(0, 0);
  ctx.lineTo(s * 2.2, s * 1.1);
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(s * 2.2, -s * 1.1, s * 0.55, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(s * 2.2, s * 1.1, s * 0.55, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();
  ctx.restore();
}

function drawCutLines(ctx, settings, scale) {
  const { pageWidth, pageHeight, margin } = settings;
  const halfW = margin + (pageWidth - 2 * margin) / 2;
  const halfH = margin + (pageHeight - 2 * margin) / 2;

  ctx.save();
  ctx.strokeStyle = '#9aa0a6';
  ctx.lineWidth = 1;
  ctx.setLineDash([6, 4]);

  ctx.beginPath();
  ctx.moveTo(halfW * scale, margin * scale);
  ctx.lineTo(halfW * scale, (pageHeight - margin) * scale);
  ctx.stroke();

  ctx.beginPath();
  ctx.moveTo(margin * scale, halfH * scale);
  ctx.lineTo((pageWidth - margin) * scale, halfH * scale);
  ctx.stroke();

  ctx.setLineDash([]);
  ctx.restore();

  drawScissors(ctx, halfW, margin, scale, Math.PI / 2);
  drawScissors(ctx, halfW, pageHeight - margin, scale, -Math.PI / 2);
  drawScissors(ctx, margin, halfH, scale, 0);
  drawScissors(ctx, pageWidth - margin, halfH, scale, Math.PI);
}

function drawCropMarks(ctx, settings, scale) {
  const { pageWidth, pageHeight, margin } = settings;
  const len = 3; // mm
  ctx.save();
  ctx.strokeStyle = '#c6c9cd';
  ctx.lineWidth = 1;
  const corners = [
    [margin, margin, 1, 1],
    [pageWidth - margin, margin, -1, 1],
    [margin, pageHeight - margin, 1, -1],
    [pageWidth - margin, pageHeight - margin, -1, -1],
  ];
  corners.forEach(([cx, cy, dx, dy]) => {
    ctx.beginPath();
    ctx.moveTo(cx * scale, cy * scale);
    ctx.lineTo((cx + dx * len) * scale, cy * scale);
    ctx.moveTo(cx * scale, cy * scale);
    ctx.lineTo(cx * scale, (cy + dy * len) * scale);
    ctx.stroke();
  });
  ctx.restore();
}

function drawPhoto(ctx, pos, scale, image, placeholderIndex) {
  const x = pos.x * scale;
  const y = pos.y * scale;
  const w = pos.w * scale;
  const h = pos.h * scale;

  if (image && image.el) {
    ctx.drawImage(image.el, x, y, w, h);
    ctx.strokeStyle = '#d0d3d7';
    ctx.lineWidth = 1;
    ctx.strokeRect(x, y, w, h);
  } else {
    ctx.fillStyle = '#fafafa';
    ctx.fillRect(x, y, w, h);
    ctx.strokeStyle = '#d0d3d7';
    ctx.lineWidth = 1;
    ctx.strokeRect(x, y, w, h);
    ctx.fillStyle = '#c2c5c9';
    ctx.font = `${Math.max(9, w * 0.18)}px sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(String(placeholderIndex + 1), x + w / 2, y + h / 2);
  }
}

function renderPreview(canvas, settings, layout, pageImages, options) {
  if (!canvas) return;
  const scale = PREVIEW_SCALE;
  const { pageWidth, pageHeight } = settings;
  canvas.width = pageWidth * scale;
  canvas.height = pageHeight * scale;
  const ctx = canvas.getContext('2d');

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  if (options.uploadMode === 'single-center') {
    const pos = {
      x: pageWidth / 2 - settings.photoWidth / 2,
      y: pageHeight / 2 - settings.photoHeight / 2,
      w: settings.photoWidth,
      h: settings.photoHeight,
    };
    drawPhoto(ctx, pos, scale, pageImages[0], 0);
  } else {
    layout.positions.forEach((pos, i) => drawPhoto(ctx, pos, scale, pageImages[i], i));
  }

  drawCutLines(ctx, settings, scale);
  if (options.showCropMarks) drawCropMarks(ctx, settings, scale);

  ctx.strokeStyle = '#333333';
  ctx.lineWidth = 1.5;
  ctx.strokeRect(0.5, 0.5, canvas.width - 1, canvas.height - 1);
}

/* ------------------------------------------------------------------ */
/*  PDF generation — mirrors the canvas drawing, in millimetres        */
/* ------------------------------------------------------------------ */

function drawScissorsPDF(doc, x, y, rotationDeg) {
  doc.saveGraphicsState && doc.saveGraphicsState();
  // jsPDF has no native rotate-around-point helper on lines, so we
  // compute rotated endpoints manually for a tiny scissor glyph.
  const s = 2.2;
  const rad = (rotationDeg * Math.PI) / 180;
  const rot = (px, py) => ({
    x: x + px * Math.cos(rad) - py * Math.sin(rad),
    y: y + px * Math.sin(rad) + py * Math.cos(rad),
  });
  const p1 = rot(s * 2.2, -s * 1.1);
  const p2 = rot(s * 2.2, s * 1.1);
  doc.setDrawColor(154, 160, 166);
  doc.setLineWidth(0.15);
  doc.line(x, y, p1.x, p1.y);
  doc.line(x, y, p2.x, p2.y);
  doc.circle(p1.x, p1.y, s * 0.35, 'S');
  doc.circle(p2.x, p2.y, s * 0.35, 'S');
  doc.restoreGraphicsState && doc.restoreGraphicsState();
}

function generatePDF(settings, layout, pages, uploadMode) {
  const orientation = settings.pageWidth >= settings.pageHeight ? 'landscape' : 'portrait';
  const doc = new jsPDF({
    orientation,
    unit: 'mm',
    format: [settings.pageWidth, settings.pageHeight],
  });

  const { pageWidth, pageHeight, margin } = settings;
  const halfW = margin + (pageWidth - 2 * margin) / 2;
  const halfH = margin + (pageHeight - 2 * margin) / 2;

  pages.forEach((pageImages, pIdx) => {
    if (pIdx > 0) doc.addPage([pageWidth, pageHeight], orientation);

    // Photos
    if (uploadMode === 'single-center') {
      const pos = {
        x: pageWidth / 2 - settings.photoWidth / 2,
        y: pageHeight / 2 - settings.photoHeight / 2,
        w: settings.photoWidth,
        h: settings.photoHeight,
      };
      const img = pageImages[0];
      if (img) doc.addImage(img.src, getImageFormat(img.src), pos.x, pos.y, pos.w, pos.h);
      else {
        doc.setDrawColor(208, 211, 215);
        doc.rect(pos.x, pos.y, pos.w, pos.h);
      }
    } else {
      layout.positions.forEach((pos, i) => {
        const img = pageImages[i];
        if (img) {
          doc.addImage(img.src, getImageFormat(img.src), pos.x, pos.y, pos.w, pos.h);
        } else {
          doc.setDrawColor(208, 211, 215);
          doc.rect(pos.x, pos.y, pos.w, pos.h);
        }
      });
    }

    // Outer border
    doc.setDrawColor(51, 51, 51);
    doc.setLineWidth(0.3);
    doc.rect(0, 0, pageWidth, pageHeight);

    // Center dashed cut lines
    doc.setDrawColor(154, 160, 166);
    doc.setLineWidth(0.15);
    doc.setLineDashPattern([1.5, 1], 0);
    doc.line(halfW, margin, halfW, pageHeight - margin);
    doc.line(margin, halfH, pageWidth - margin, halfH);
    doc.setLineDashPattern([], 0);

    // Scissors at both ends of each cut line
    drawScissorsPDF(doc, halfW, margin, 90);
    drawScissorsPDF(doc, halfW, pageHeight - margin, -90);
    drawScissorsPDF(doc, margin, halfH, 0);
    drawScissorsPDF(doc, pageWidth - margin, halfH, 180);
  });

  doc.save('passport-photos.pdf');
}

/* ------------------------------------------------------------------ */
/*  Image loading                                                      */
/* ------------------------------------------------------------------ */

function loadImageFromDataUrl(src, name) {
  return new Promise((resolve) => {
    const el = new Image();
    el.onload = () => resolve({ id: `${name}-${Date.now()}-${Math.random()}`, name, src, el });
    el.onerror = () => resolve({ id: `${name}-${Date.now()}-${Math.random()}`, name, src, el: null });
    el.src = src;
  });
}

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

const IMAGE_EXT = /\.(png|jpe?g|webp)$/i;

async function extractImagesFromZip(file) {
  const zip = await JSZip.loadAsync(file);
  const entries = Object.values(zip.files)
    .filter((f) => !f.dir && IMAGE_EXT.test(f.name))
    .sort((a, b) => a.name.localeCompare(b.name));

  const images = [];
  for (const entry of entries) {
    const base64 = await entry.async('base64');
    const ext = entry.name.split('.').pop().toLowerCase();
    const mime = ext === 'png' ? 'image/png' : ext === 'webp' ? 'image/webp' : 'image/jpeg';
    const dataUrl = `data:${mime};base64,${base64}`;
    images.push(await loadImageFromDataUrl(dataUrl, entry.name));
  }
  return images;
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

const inputStyle = {
  width: '100%',
  padding: '6px 8px',
  border: '1px solid #d0d3d7',
  borderRadius: 6,
  fontSize: 13,
  boxSizing: 'border-box',
};
const labelStyle = { fontSize: 12, color: '#555', display: 'block', marginBottom: 4 };
const fieldWrap = { marginBottom: 12 };
const panelStyle = {
  background: '#fff',
  border: '1px solid #e4e6e9',
  borderRadius: 10,
  padding: 16,
};
const buttonBase = {
  padding: '9px 14px',
  borderRadius: 8,
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
  border: '1px solid transparent',
};

export default function PassportPhotoLayoutGenerator() {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [orientation, setOrientation] = useState('landscape');
  const [uploadMode, setUploadMode] = useState('bulk');
  const [images, setImages] = useState([]);
  const [pageIndex, setPageIndex] = useState(0);
  const [showCropMarks, setShowCropMarks] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);

  const layout = useMemo(() => calculateFullLayout(settings), [settings]);
  const pages = useMemo(() => buildPages(images, layout, uploadMode), [images, layout, uploadMode]);

  useEffect(() => {
    if (pageIndex > pages.length - 1) setPageIndex(0);
  }, [pages, pageIndex]);

  useEffect(() => {
    renderPreview(canvasRef.current, settings, layout, pages[pageIndex] || [], {
      uploadMode,
      showCropMarks,
    });
  }, [settings, layout, pages, pageIndex, uploadMode, showCropMarks]);

  const updateSetting = useCallback((key, value) => {
    setSettings((s) => ({ ...s, [key]: value }));
  }, []);

  const toggleOrientation = () => {
    setOrientation((o) => (o === 'landscape' ? 'portrait' : 'landscape'));
    setSettings((s) => ({ ...s, pageWidth: s.pageHeight, pageHeight: s.pageWidth }));
  };

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setIsProcessing(true);
    try {
      if (uploadMode === 'zip') {
        const zipFile = files.find((f) => /\.zip$/i.test(f.name)) || files[0];
        const extracted = await extractImagesFromZip(zipFile);
        setImages(extracted);
      } else {
        const loaded = [];
        for (const file of files) {
          const dataUrl = await readFileAsDataUrl(file);
          loaded.push(await loadImageFromDataUrl(dataUrl, file.name));
        }
        // single-repeat / single-center only ever use the first image
        setImages(uploadMode === 'bulk' ? loaded : loaded.slice(0, 1));
      }
      setPageIndex(0);
    } finally {
      setIsProcessing(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const clearImages = () => {
    setImages([]);
    setPageIndex(0);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDownload = () => {
    if (images.length === 0) {
      alert('Upload at least one photo first.');
      return;
    }
    generatePDF(settings, layout, pages, uploadMode);
  };

  const printableW = settings.pageWidth - settings.margin * 2;
  const printableH = settings.pageHeight - settings.margin * 2;
  const photosPerPage =
    uploadMode === 'single-center' ? 1 : layout.positions.length;

  return (
    <div style={{ padding: 20, fontFamily: 'system-ui, sans-serif', color: '#1c1e21', maxWidth: 1400, margin: '0 auto' }}>
      <h1 style={{ fontSize: 22, marginBottom: 4 }}>Passport Photo Layout Generator</h1>
      <p style={{ color: '#666', fontSize: 13, marginTop: 0, marginBottom: 20 }}>
        Arrange passport photos on a print sheet, split into 4 cuttable sections, and export a print-ready PDF.
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr 260px', gap: 20, alignItems: 'start' }}>
        {/* -------- Settings panel -------- */}
        <div style={panelStyle}>
          <h3 style={{ fontSize: 14, marginTop: 0 }}>Settings</h3>

          <div style={fieldWrap}>
            <label style={labelStyle}>Photo width (mm)</label>
            <input
              style={inputStyle}
              type="number"
              value={settings.photoWidth}
              onChange={(e) => updateSetting('photoWidth', Number(e.target.value) || 0)}
            />
          </div>
          <div style={fieldWrap}>
            <label style={labelStyle}>Photo height (mm)</label>
            <input
              style={inputStyle}
              type="number"
              value={settings.photoHeight}
              onChange={(e) => updateSetting('photoHeight', Number(e.target.value) || 0)}
            />
          </div>
          <div style={fieldWrap}>
            <label style={labelStyle}>Gap between photos (mm)</label>
            <input
              style={inputStyle}
              type="number"
              value={settings.gap}
              onChange={(e) => updateSetting('gap', Number(e.target.value) || 0)}
            />
          </div>
          <div style={fieldWrap}>
            <label style={labelStyle}>Outer margin (mm)</label>
            <input
              style={inputStyle}
              type="number"
              value={settings.margin}
              onChange={(e) => updateSetting('margin', Number(e.target.value) || 0)}
            />
          </div>
          <div style={fieldWrap}>
            <label style={labelStyle}>Page width (mm)</label>
            <input
              style={inputStyle}
              type="number"
              value={settings.pageWidth}
              onChange={(e) => updateSetting('pageWidth', Number(e.target.value) || 0)}
            />
          </div>
          <div style={fieldWrap}>
            <label style={labelStyle}>Page height (mm)</label>
            <input
              style={inputStyle}
              type="number"
              value={settings.pageHeight}
              onChange={(e) => updateSetting('pageHeight', Number(e.target.value) || 0)}
            />
          </div>
          <div style={fieldWrap}>
            <label style={labelStyle}>Orientation</label>
            <button
              style={{ ...buttonBase, background: '#f2f3f5', width: '100%' }}
              onClick={toggleOrientation}
              type="button"
            >
              {orientation === 'landscape' ? 'Landscape' : 'Portrait'} (swap)
            </button>
          </div>
          <div style={fieldWrap}>
            <label style={labelStyle}>Photos per box (max {layout.maxFit.count} — {layout.maxFit.cols}×{layout.maxFit.rows})</label>
            <input
              style={inputStyle}
              type="number"
              min={1}
              max={layout.maxFit.count}
              placeholder={`Auto (${layout.maxFit.count})`}
              value={settings.photosPerSectionOverride || ''}
              onChange={(e) => {
                const v = Number(e.target.value);
                updateSetting('photosPerSectionOverride', v > 0 ? v : null);
              }}
            />
          </div>
          <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: 6 }}>
            <input type="checkbox" checked={showCropMarks} onChange={(e) => setShowCropMarks(e.target.checked)} />
            Show crop marks
          </label>
        </div>

        {/* -------- Preview -------- */}
        <div style={panelStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <h3 style={{ fontSize: 14, margin: 0 }}>Live Preview</h3>
            {pages.length > 1 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                <button
                  style={{ ...buttonBase, background: '#f2f3f5', padding: '4px 10px' }}
                  onClick={() => setPageIndex((p) => Math.max(0, p - 1))}
                  disabled={pageIndex === 0}
                  type="button"
                >
                  ◀
                </button>
                Page {pageIndex + 1} / {pages.length}
                <button
                  style={{ ...buttonBase, background: '#f2f3f5', padding: '4px 10px' }}
                  onClick={() => setPageIndex((p) => Math.min(pages.length - 1, p + 1))}
                  disabled={pageIndex === pages.length - 1}
                  type="button"
                >
                  ▶
                </button>
              </div>
            )}
          </div>
          <div style={{ overflow: 'auto', border: '1px solid #eee', borderRadius: 8, padding: 12, background: '#f7f8f9' }}>
            <canvas ref={canvasRef} style={{ display: 'block', margin: '0 auto', maxWidth: '100%', height: 'auto' }} />
          </div>

          <div style={{ display: 'flex', gap: 10, marginTop: 16, flexWrap: 'wrap' }}>
            <label style={{ ...buttonBase, background: '#1a73e8', color: '#fff' }}>
              {isProcessing ? 'Processing…' : 'Upload Photos'}
              <input
                ref={fileInputRef}
                type="file"
                accept={uploadMode === 'zip' ? '.zip' : 'image/*'}
                multiple={uploadMode === 'bulk'}
                onChange={handleFiles}
                style={{ display: 'none' }}
              />
            </label>
            <button
              style={{ ...buttonBase, background: '#1a7f37', color: '#fff' }}
              onClick={handleDownload}
              type="button"
            >
              Generate &amp; Download PDF
            </button>
            <button
              style={{ ...buttonBase, background: '#f2f3f5' }}
              onClick={() =>
                renderPreview(canvasRef.current, settings, layout, pages[pageIndex] || [], { uploadMode, showCropMarks })
              }
              type="button"
            >
              Refresh Preview
            </button>
            <button
              style={{ ...buttonBase, background: '#fdecea', color: '#c0392b' }}
              onClick={clearImages}
              type="button"
            >
              Clear Images
            </button>
          </div>

          <div style={{ marginTop: 14 }}>
            <label style={labelStyle}>Upload mode</label>
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: 13 }}>
              {[
                ['single-repeat', 'Single image → repeat in all slots'],
                ['single-center', 'Single image → center only'],
                ['bulk', 'Bulk images → fill sequentially'],
                ['zip', 'ZIP of images → fill sequentially'],
              ].map(([value, text]) => (
                <label key={value} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <input
                    type="radio"
                    name="uploadMode"
                    value={value}
                    checked={uploadMode === value}
                    onChange={() => {
                      setUploadMode(value);
                      clearImages();
                    }}
                  />
                  {text}
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* -------- Info panel -------- */}
        <div style={panelStyle}>
          <h3 style={{ fontSize: 14, marginTop: 0 }}>Layout Info</h3>
          <InfoRow label="Paper size" value={`${settings.pageWidth} × ${settings.pageHeight} mm`} />
          <InfoRow label="Printable area" value={`${printableW} × ${printableH} mm`} />
          <InfoRow label="Photo size" value={`${settings.photoWidth} × ${settings.photoHeight} mm`} />
          <InfoRow label="Gap" value={`${settings.gap} mm`} />
          <InfoRow label="Outer margin" value={`${settings.margin} mm`} />
          <InfoRow label="Max fit per box" value={`${layout.maxFit.cols} × ${layout.maxFit.rows} = ${layout.maxFit.count}`} />
          <InfoRow label="Photos per box (in use)" value={photosPerPage === 1 ? photosPerPage : layout.perSection} />
          <InfoRow label="Total sheet capacity" value={photosPerPage === 1 ? 1 : layout.perSection * layout.sections.length} />
          <InfoRow label="Images loaded" value={images.length} />
          <InfoRow label="Total pages" value={pages.length} />
        </div>
      </div>
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, padding: '6px 0', borderBottom: '1px solid #f0f1f2' }}>
      <span style={{ color: '#777' }}>{label}</span>
      <span style={{ fontWeight: 600 }}>{value}</span>
    </div>
  );
}
